import * as React from "react";
import { AnalyticsDashboardProps, SubscriptionAnalyticsData, AnalyticsFilter } from "../../types/stripe";
import { ExportButton, RefreshDataButton, AnalyticsSpinner } from "../shared";
import MRRChart from "./MRRChart";
import CohortRetentionTable from "./CohortRetentionTable";
import MetricsCards from "../MetricsCards";
import RevenueCalendar from "./RevenueCalendar";
import MonthlyBillingProgress from "./MonthlyBillingProgress";
import BillingEventsList from "./BillingEventsList";

/**
 * SubscriptionAnalyticsDashboard Component
 * 
 * Main subscription metrics dashboard with MRR, churn, and retention analysis
 */
interface SubscriptionAnalyticsDashboardProps extends AnalyticsDashboardProps {
  data?: SubscriptionAnalyticsData;
  onDataRefresh?: () => Promise<void>;
  isRefreshing?: boolean;
  error?: string | null;
  currency?: string;
}

const SubscriptionAnalyticsDashboard: React.FC<SubscriptionAnalyticsDashboardProps> = ({
  data,
  filters,
  onFiltersChange,
  onDataRefresh,
  isRefreshing = false,
  error = null,
  currency = 'USD',
  autoRefresh = false,
  refreshInterval = 300,
  onExport,
  className = "",
  id
}) => {
  const [isLoading, setIsLoading] = React.useState(!data && !error);
  const [activeTab, setActiveTab] = React.useState<'overview' | 'cohorts' | 'churn' | 'calendar'>('overview');
  const [selectedCalendarDate, setSelectedCalendarDate] = React.useState<string | null>(null);

  const defaultFilters: AnalyticsFilter = {
    start_date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    granularity: 'month',
    currency: currency.toLowerCase() as any
  };

  const currentFilters = filters || defaultFilters;

  const handleRefresh = async () => {
    if (onDataRefresh) {
      try {
        setIsLoading(true);
        await onDataRefresh();
      } catch (error) {
        console.error('Error refreshing data:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Transform subscription metrics to MetricData format
  const metricCards = React.useMemo(() => {
    if (!data?.metrics) return [];
    
    return [
      {
        id: 'mrr',
        label: 'Monthly Recurring Revenue',
        value: data.metrics.mrr,
        previousValue: data.metrics.previous_mrr,
        icon: 'fas fa-chart-line',
        variant: 'primary' as const,
        format: 'currency' as const
      },
      {
        id: 'arr',
        label: 'Annual Recurring Revenue',
        value: data.metrics.arr,
        previousValue: data.metrics.previous_arr,
        icon: 'fas fa-calendar-alt',
        variant: 'success' as const,
        format: 'currency' as const
      },
      {
        id: 'active_subs',
        label: 'Active Subscriptions',
        value: data.metrics.active_subscriptions,
        previousValue: data.metrics.previous_active_subscriptions,
        icon: 'fas fa-users',
        variant: 'info' as const,
        format: 'number' as const
      },
      {
        id: 'churn_rate',
        label: 'Churn Rate',
        value: data.metrics.churn_rate,
        previousValue: data.metrics.previous_churn_rate,
        icon: 'fas fa-user-times',
        variant: 'danger' as const,
        format: 'percentage' as const
      }
    ];
  }, [data?.metrics]);

  if (isLoading && !data) {
    return (
      <div className={`subscription-analytics-dashboard ${className}`.trim()} id={id}>
        <AnalyticsSpinner
          overlay={true}
          size="lg"
          message="Loading subscription analytics..."
        />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={`subscription-analytics-dashboard ${className}`.trim()} id={id}>
        <div className="container-fluid py-4">
          <div className="alert alert-danger">
            <h4 className="alert-heading">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              Error Loading Subscription Analytics
            </h4>
            <p className="mb-0">{error}</p>
            <hr />
            <div className="mb-0">
              <button
                className="btn btn-outline-danger"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <i className="fas fa-redo me-2" aria-hidden="true"></i>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`subscription-analytics-dashboard ${className}`.trim()} id={id}>
      {/* Dashboard Header */}
      <div className="dashboard-header mb-4">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="h3 mb-0">
                <i className="fas fa-sync me-2 text-success" aria-hidden="true"></i>
                Subscription Analytics
              </h1>
              <p className="text-muted mb-0">
                MRR, churn, and customer retention insights
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="btn-toolbar justify-content-md-end" role="toolbar">
                <div className="btn-group me-2" role="group">
                  <RefreshDataButton
                    onRefresh={handleRefresh}
                    isRefreshing={isRefreshing}
                    autoRefresh={autoRefresh}
                    refreshInterval={refreshInterval}
                    lastRefreshed={data ? new Date() : undefined}
                  />
                </div>
                <div className="btn-group" role="group">
                  <ExportButton
                    onExport={onExport || (() => {})}
                    disabled={!data}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        {/* Metrics Cards */}
        {metricCards.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <MetricsCards
                metrics={metricCards}
                isLoading={isRefreshing}
                currency={currency}
              />
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="row mb-4">
          <div className="col-12">
            <ul className="nav nav-tabs" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('overview')}
                >
                  <i className="fas fa-chart-area me-1" aria-hidden="true"></i>
                  MRR Overview
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'cohorts' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('cohorts')}
                >
                  <i className="fas fa-table me-1" aria-hidden="true"></i>
                  Cohort Analysis
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'churn' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('churn')}
                >
                  <i className="fas fa-user-times me-1" aria-hidden="true"></i>
                  Churn Analysis
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'calendar' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('calendar')}
                >
                  <i className="fas fa-calendar-alt me-1" aria-hidden="true"></i>
                  Revenue Calendar
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* MRR Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-pane fade show active">
              <div className="row">
                <div className="col-12">
                  {data?.subscription_chart ? (
                    <MRRChart
                      data={data.subscription_chart}
                      currency={currency}
                      isLoading={isRefreshing}
                    />
                  ) : (
                    <div className="card">
                      <div className="card-body text-center py-5 text-muted">
                        <i className="fas fa-chart-line fa-2x mb-3" aria-hidden="true"></i>
                        <p className="mb-0">No MRR data available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cohort Analysis Tab */}
          {activeTab === 'cohorts' && (
            <div className="tab-pane fade show active">
              <div className="row">
                <div className="col-12">
                  {data?.cohort_retention ? (
                    <CohortRetentionTable
                      cohorts={data.cohort_retention}
                      isLoading={isRefreshing}
                    />
                  ) : (
                    <div className="card">
                      <div className="card-body text-center py-5 text-muted">
                        <i className="fas fa-table fa-2x mb-3" aria-hidden="true"></i>
                        <p className="mb-0">No cohort data available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Churn Analysis Tab */}
          {activeTab === 'churn' && (
            <div className="tab-pane fade show active">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">
                        <i className="fas fa-user-times me-2" aria-hidden="true"></i>
                        Churn Analysis
                      </h6>
                    </div>
                    <div className="card-body text-center py-5 text-muted">
                      <i className="fas fa-construction fa-2x mb-3" aria-hidden="true"></i>
                      <p className="mb-0">Churn analysis components coming soon</p>
                      <small className="text-muted">
                        Will include churn reasons, cohort churn rates, and prediction models
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Revenue Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="tab-pane fade show active">
              <div className="row mb-4">
                {/* Monthly Billing Progress */}
                <div className="col-xl-4 col-lg-6 mb-4">
                  <MonthlyBillingProgress
                    isLoading={isRefreshing}
                    filters={{
                      plan_filter: currentFilters.plan_id,
                      utm_source_filter: currentFilters.utm_source
                    }}
                    height={350}
                  />
                </div>

                {/* Billing Events List */}
                <div className="col-xl-8 col-lg-6 mb-4">
                  <BillingEventsList
                    selectedDate={selectedCalendarDate}
                    isLoading={isRefreshing}
                    filters={{
                      plan_filter: currentFilters.plan_id,
                      utm_source_filter: currentFilters.utm_source
                    }}
                    onClose={() => setSelectedCalendarDate(null)}
                    onSubscriptionClick={(_subscriptionId) => {
                    }}
                    onInvoiceClick={(_invoiceId) => {
                    }}
                  />
                </div>
              </div>

              {/* Revenue Calendar */}
              <div className="row">
                <div className="col-12">
                  <RevenueCalendar
                    isLoading={isRefreshing}
                    // RevenueCalendar owns its own date range (driven by the visible
                    // FullCalendar grid) so future billing dates are actually requested --
                    // the dashboard's start_date/end_date filters default to "90 days ago ->
                    // today" and would never include them. Only forward plan/utm filters.
                    filters={{
                      plan_filter: currentFilters.plan_id,
                      utm_source_filter: currentFilters.utm_source
                    }}
                    height={700}
                    onDateClick={(date) => {
                      setSelectedCalendarDate(date);
                      // Scroll to billing events list
                      setTimeout(() => {
                        const billingList = document.querySelector('.billing-events-list');
                        if (billingList) {
                          billingList.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    onEventClick={(event) => {
                      setSelectedCalendarDate(event.start);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .subscription-analytics-dashboard {
          min-height: 100vh;
          background-color: var(--bs-gray-50);
        }

        .dashboard-header {
          background: linear-gradient(135deg, var(--bs-success) 0%, var(--bs-info) 100%);
          color: white;
          padding: 2rem 0;
          margin-bottom: 0 !important;
        }

        .dashboard-header .text-muted {
          color: rgba(255, 255, 255, 0.8) !important;
        }

        .nav-tabs {
          border-bottom: 2px solid var(--bs-gray-200);
        }

        .nav-tabs .nav-link {
          border: none;
          color: var(--bs-gray-600);
          font-weight: 500;
          padding: 1rem 1.5rem;
        }

        .nav-tabs .nav-link:hover {
          border: none;
          color: var(--bs-success);
        }

        .nav-tabs .nav-link.active {
          border: none;
          border-bottom: 3px solid var(--bs-success);
          color: var(--bs-success);
          background: none;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .dashboard-header .btn-toolbar {
            justify-content: center !important;
            margin-top: 1rem;
          }

          .nav-tabs {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

SubscriptionAnalyticsDashboard.displayName = 'SubscriptionAnalyticsDashboard';

export default SubscriptionAnalyticsDashboard;